import React, {
	useState,
	useContext,
	useEffect,
	useCallback,
	useRef,
} from 'react';
import Loading from 'tbk-components/src/components/Loading';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import RawHTML from 'tbk-components/src/components/RawHTML';
import Modal, { IModalI18n } from 'tbk-components/src/components/Modal';

export interface ICareersProfileModalI18n extends IModalI18n {
	title: string;
	errorTemplate: string;
}
export interface ICareersProfileFormProps {
	fields?: string[];
	onCancel?: () => void;
	modalI18n?: ICareersProfileModalI18n;
	context?: {
		Api: any;
	};
}

const CareersProfileForm: React.FC<ICareersProfileFormProps> = withAppContext(
	({ fields, modalI18n, onCancel = () => void 0, context }) => {
		const { Api } = useContext(context);
		const [formMarkup, setFormMarkup] = useState<string>(null);
		const [error, setError] = useState<string>(null);
		const [profileUpdateError, setProfileUpdateError] =
			useState<string>(null);
		const [busy, setBusy] = useState<boolean>(false);
		const ref = useRef(null);

		useEffect(() => {
			Api.getUserProfileForm({ fields })
				.then((form: string) => {
					setFormMarkup(form);
				})
				.catch((error: string) => {
					setError(error);
				});
		}, [fields]);

		const onSubmit = useCallback(() => {
			// @ts-ignore
			const acf = (window?.acf as any) || null;
			// @ts-ignore
			const $ = (window?.jQuery as any) || null;
			if (formMarkup && acf && $) {
				// Good information in https://support.advancedcustomfields.com/forums/topic/frontend-form-post-via-ajax/
				// on how to submit the form via ajax, and I borrowed heavily from that, in particular, from
				// https://support.advancedcustomfields.com/forums/topic/frontend-form-post-via-ajax/page/2/#post-126726
				const form = document.getElementById(
					'acf-form',
				) as HTMLFormElement;
				if (form) {
					const submitButton = form.querySelector(
						'input[type="submit"]',
					) as HTMLInputElement;
					setBusy(true);

					if (submitButton.getAttribute('disabled')) {
						// Prevent double submissions.
						return;
					}

					setProfileUpdateError(null);
					submitButton.setAttribute('disabled', 'disabled');

					const args = {
						form: $(form),
						reset: true,
						success: ($form: any) => {
							// Fix for Safari Webkit – empty file inputs kill the browser
							// https://stackoverflow.com/a/49827426/586823
							const $fileInputs = $form.find(
								'input[type="file"]:not([disabled])',
							);
							// @ts-ignore
							$fileInputs.each(
								(index: number, input: HTMLInputElement) => {
									if (input.files.length > 0) {
										return;
									}
									$(input).prop('disabled', true);
								},
							);

							var formData = new FormData($form[0]);

							// Re-enable empty file $fileInputs
							$fileInputs.prop('disabled', false);

							acf.lockForm($form);

							$.ajax({
								url: window.location.href,
								method: 'post',
								data: formData,
								cache: false,
								processData: false,
								contentType: false,
							})
								.done((response) => {
									// We'll leave the form locked and the submit button disabled, and then
									// just reload the page.  That gets the new values from the server to show.
									window.location.reload();
								})
								.fail((response) => {
									// This handles the case of a server error while trying to save.
									submitButton.removeAttribute('disabled');
									acf.unlockForm($form);
									setProfileUpdateError(response.statusText);
									setBusy(false);
								});
						},
						failure: () => {
							submitButton.removeAttribute('disabled');
							setBusy(false);
						},
					};

					acf.validateForm(args);
				}
			}
		}, [formMarkup]);

		useEffect(() => {
			// @ts-ignore
			const acf = (window?.acf as any) || null;
			// @ts-ignore
			const $ = (window?.jQuery as any) || null;
			if (formMarkup && acf && $) {
				acf.doAction('prepare');
				$('#acf-form').find('.acf-form-submit').hide();
				$('#acf-form').on('submit', (e) => {
					e.preventDefault();
					onSubmit();
				});
			}

			if (!ref.current) {
				return;
			}

			const fileUploadFields =
				ref.current.querySelectorAll('.acf-field-file');
			if (fileUploadFields.length) {
				fileUploadFields.forEach((field) => {
					const description = field.querySelector('.description');
					if (description) {
						field.appendChild(description);
					}
				});
			}
		}, [formMarkup, onSubmit]);

		return (
			<Modal
				onCancel={onCancel}
				onSubmit={onSubmit}
				showSubmit={!!formMarkup}
				showCancel={!!formMarkup}
				i18n={modalI18n}
				title={modalI18n.title}
				busy={busy}
				className="calian-careers-profile-form"
				modalClassNames={['modal-xl']}
				headerClassNames={['p-lg-5 border-0']}
				bodyClassNames={['py-2 px-2 px-lg-5']}
				closeOnOutsideClick={false}
				trapFocus={!!formMarkup}
			>
				{!(formMarkup || error) && <Loading />}
				{error ? (
					<p>{error}</p>
				) : (
					<div className="profile-form-wrapper" ref={ref}>
						<RawHTML html={formMarkup} />
						{profileUpdateError && (
							<div className="acf-notice -error acf-error-message">
								<p>
									{modalI18n.errorTemplate.replace(
										'%s',
										profileUpdateError,
									)}
								</p>
							</div>
						)}
					</div>
				)}
			</Modal>
		);
	},
);

export default CareersProfileForm;
